import { Injectable, inject } from '@angular/core';
import { AuthenticationService } from '@omni/services/authentication.service';
import { BrandOfflineService } from '@omni/services/brand/brand.service';
import { DeviceService } from '@omni/services/device/device.service';
import { IoConfigurationFeatureCodes } from '@omni/classes/authentication/user.class';
import { VoiceNoteService } from '@omni/services/voice-note/voice-note.service';
import { recordingSections } from './audio-recorder';
import { RecordingStore } from './recording-store';
import { RecognitionCancelledError, RecordingTranscriber } from './recording-transcriber';
import { SpeechRecogniserService } from './speech-recogniser.service';
import { discardOrphans } from './recording-cleanup';
import { buildSpeechPhrases, pickConfiguredPhrases } from './speech-phrases';
import { isDueForRetry, shouldSweep } from './transcription-schedule';

/**
 * Reads the recordings waiting on this device.
 *
 * A recording is made where recognition could not be reached, so nothing about it
 * is the rep's to start. It is read when the device next has a connection, one at
 * a time, and never while the rep is dictating — the device has one recogniser and
 * a live call has a better claim on it than a recording already safely held.
 *
 * Failure counts live here rather than on the note: a reason from yesterday tells
 * the rep nothing about today, and a restart should not surface one.
 */
@Injectable({ providedIn: 'root' })
export class TranscriptionSweepService {
  readonly #device = inject(DeviceService);
  readonly #auth = inject(AuthenticationService);
  readonly #brands = inject(BrandOfflineService);
  readonly #notes = inject(VoiceNoteService);
  readonly #recordings = inject(RecordingStore);
  readonly #transcriber = inject(RecordingTranscriber);
  readonly #recognisers = inject(SpeechRecogniserService);

  /** Attempts so far, and when the last one failed, per recording. */
  readonly #failures = new Map<string, { count: number; lastFailedAt: number }>();
  /** The rep is dictating; the recogniser is theirs. */
  #capturing = false;
  #reading = false;

  public start(): void {
    // A connection returning is the moment worth acting on. It also clears the
    // waits: a service that was unreachable because the device was, is not a
    // service that has been failing.
    this.#device.isOfflineObservable.subscribe((offline) => {
      if (!offline) {
        this.#failures.clear();
        void this.sweep();
      }
    });
    void this.sweep();
  }

  /** Held while the rep dictates, so a sweep does not take the recogniser. */
  public capturing(capturing: boolean): void {
    this.#capturing = capturing;
    if (!capturing) {
      void this.sweep();
    }
  }

  /** Read the recordings that are due, one at a time. */
  public async sweep(): Promise<void> {
    const waiting = await this.#discardOrphans();
    if (!shouldSweep({
      online: !this.#device.isOffline,
      capturing: this.#capturing,
      reading: this.#reading,
      waiting: waiting.length,
    })) {
      return;
    }

    this.#reading = true;
    try {
      for (const noteId of waiting) {
        // Re-checked each time round: the rep may have started dictating, or the
        // connection may have gone, since the recording before this one.
        if (this.#capturing || this.#device.isOffline) {
          return;
        }
        if (!this.#isDue(noteId)) {
          continue;
        }
        await this.#read(noteId);
      }
    } finally {
      this.#reading = false;
    }
  }

  /**
   * Runs before the sweep proper and without regard to the connection: a
   * recording with no note is unreachable, and letting go of it needs neither a
   * recogniser nor a network.
   */
  async #discardOrphans(): Promise<string[]> {
    return discardOrphans(
      await this.#recordings.heldNoteIds(),
      () => this.#notes.storedNoteIds(),
      (noteId) => this.#recordings.remove(noteId),
    );
  }

  #isDue(noteId: string): boolean {
    const failure = this.#failures.get(noteId);
    return isDueForRetry({
      failures: failure?.count ?? 0,
      lastFailedAt: failure?.lastFailedAt ?? null,
      now: Date.now(),
    });
  }

  async #read(noteId: string): Promise<void> {
    const held = await this.#recordings.get(noteId);
    if (!held) {
      return;
    }
    const recogniser = await this.#recognisers.choose();
    if (!recogniser) {
      return;
    }
    try {
      const config = {
        subscriptionKey: recogniser.subscriptionKey,
        region: recogniser.region,
        language: recogniser.language,
        customEndpointId: recogniser.endpointId,
        // The same vocabulary the live path is primed with. Without it a recording
        // is read more poorly than the same words spoken online would have been.
        phrases: buildSpeechPhrases({
          configured: pickConfiguredPhrases(
            this.#auth.getIOConfigurationValue(IoConfigurationFeatureCodes.COMPANION_DICTATION_PHRASES_BU),
            this.#auth.getIOConfigurationValue(IoConfigurationFeatureCodes.COMPANION_DICTATION_PHRASES),
          ),
          brands: this.#brands.brands,
        }),
      };
      const readings = [];
      for (const section of recordingSections(held.audio, held.partLengths, held.partTypes, held.sections)) {
        readings.push({ words: await this.#transcriber.read(section.parts, config), before: section.before });
      }
      await this.#notes.applyReadings(noteId, readings);
      this.#failures.delete(noteId);
    } catch (error) {
      if (error instanceof RecognitionCancelledError) {
        this.#recognisers.reportCancelled(recogniser);
      }
      const failure = this.#failures.get(noteId);
      this.#failures.set(noteId, {
        count: (failure?.count ?? 0) + 1,
        lastFailedAt: Date.now(),
      });
      console.warn(`TranscriptionSweepService: reading ${noteId} failed; it stays held.`, error);
    }
  }
}
